import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, MapPin, Phone } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email, and a message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        subject: form.subject || null,
        message: form.message,
      });
      toast.success("Message sent. We'll be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section id="contact" data-testid="contact-section" className="py-28 lg:py-40">
      <div className="max-w-[90rem] mx-auto px-6 lg:px-12 grid grid-cols-12 gap-12 lg:gap-20">
        <div className="col-span-12 lg:col-span-5">
          <Reveal>
            <p className="overline-label mb-6" data-testid="contact-overline">Contact</p>
            <h2
              className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight"
              data-testid="contact-heading"
            >
              Start a <em className="text-primary font-normal">conversation.</em>
            </h2>
            <p className="mt-6 text-base lg:text-lg leading-relaxed text-muted-foreground max-w-md">
              Questions about Adam's Way, future communities, partnerships, or
              ways to help? We read every note.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-12 space-y-6" data-testid="contact-info">
              <div className="flex items-start gap-4">
                <MapPin size={18} className="mt-1 text-primary shrink-0" />
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=2003+S+Easton+Rd+Suite+304+Doylestown+PA+18901"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-map-link"
                  className="link-line text-sm text-foreground/80 leading-relaxed"
                >
                  2003 S Easton Rd, Suite 304<br />Doylestown, PA 18901
                </a>
              </div>
              <div className="flex items-start gap-4">
                <Phone size={18} className="mt-1 text-primary shrink-0" />
                <a href="tel:+14847185086" className="link-line text-sm text-foreground/80" data-testid="contact-phone-link">
                  (484) 718-5086
                </a>
              </div>
              <div className="flex items-start gap-4">
                <Mail size={18} className="mt-1 text-primary shrink-0" />
                <a href="mailto:josh@tnshc.org" className="link-line text-sm text-foreground/80" data-testid="contact-email-link">
                  josh@tnshc.org
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-12 arch aspect-[4/5] max-w-xs ring-1 ring-foreground/10" data-testid="contact-image">
              <img
                src="/photos/family.jpg"
                alt="Adam's Way supporters giving a thumbs up at a community event"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
        </div>

        <div className="col-span-12 lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.15}>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-7">
              <div className="grid sm:grid-cols-2 gap-7">
                <input
                  type="text"
                  placeholder="Full name"
                  value={form.name}
                  onChange={set("name")}
                  data-testid="contact-name-input"
                  className="field-input"
                />
                <input
                  type="email"
                  placeholder="Email address"
                  value={form.email}
                  onChange={set("email")}
                  data-testid="contact-email-input"
                  className="field-input"
                />
              </div>
              <input
                type="text"
                placeholder="Subject (optional)"
                value={form.subject}
                onChange={set("subject")}
                data-testid="contact-subject-input"
                className="field-input"
              />
              <textarea
                rows={5}
                placeholder="Your message"
                value={form.message}
                onChange={set("message")}
                data-testid="contact-message-input"
                className="field-input resize-none"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-10 py-4 text-sm font-medium text-primary-foreground transition-[background-color,transform] duration-300 hover:bg-lavender-deep hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Sending…" : "Send message"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
